import { DateTime } from 'luxon';

const TIMEZONE = 'America/New_York';

export const formatEST = (timestamp: number, longDate?: boolean) => {
  const date = DateTime.fromMillis(timestamp);
  let result = date.setZone(TIMEZONE).toFormat(longDate ? 'MMM d, h:mm a' : 'h:mm a');
  const diff = DateTime.now().diff(date, ['days']);
  if (diff.days > 1) {
    result = date.setZone(TIMEZONE).toFormat('MMM d, h:mm a');
  }
  return result;
};

export const formatISO = (isoDate: string, longDate?: boolean) => {
  const date = DateTime.fromISO(isoDate);
  let result = date.setZone(TIMEZONE).toFormat(longDate ? 'MMM d, h:mm a' : 'h:mm a');
  const diff = DateTime.now().diff(date, ['days']);
  if (diff.days > 1) {
    result = date.setZone(TIMEZONE).toFormat('MMM d, h:mm a');
  }
  return result;
};

export const formatAfterHours = (timestamp: number, longDate?: boolean) => {
  const date = DateTime.fromMillis(timestamp);
  let result = date.setZone(TIMEZONE).toFormat(longDate ? 'MMM d, h:mm a' : 'MMM d');
  const diff = DateTime.now().diff(date, ['days']);
  if (diff.days > 1) {
    result = date.setZone(TIMEZONE).toFormat('MMM d, h:mm a');
  }
  return result;
};

export const isToday = (date: string): boolean => {
  if (!date) return false;
  const now = DateTime.local();
  const datetime = DateTime.fromFormat(`${date}`, 'yyyy-MM-dd');
  const isToday = datetime.hasSame(now, 'day');
  return isToday;
};

export const isTodayDate = (date: Date): boolean => {
  const today = new Date();
  return (
    today.getFullYear() === date.getFullYear() &&
    today.getMonth() === date.getMonth() &&
    today.getDate() === date.getDate()
  );
};

// timestamp of last easter timezone midnight
export const getPreviousMidNightAtTimeZone = (timeZone = 'America/New_York') => {
  const now = new Date();
  const time = now.getTime();
  const nowInTimezone = new Date(
    now.toLocaleString('en-US', {
      timeZone,
    }),
  ).getTime();
  const midnight = new Date(nowInTimezone).setHours(0, 0, 0, 0);
  return midnight + (time - nowInTimezone);
};

// timestamp of last easter timezone midnight
export const getNextMidNightAtTimeZone = (timeZone = 'America/New_York') =>
  getPreviousMidNightAtTimeZone(timeZone) + 24 * 60 * 60 * 1000;

export const formatNumericDateTime = (timestamp: number, longDate?: boolean) => {
  const date = DateTime.fromFormat(timestamp.toString(), 'yyyyMMddHHmmss');
  let result = date.toFormat(longDate ? 'MMM d, h:mm a' : 'h:mm a');
  const diff = DateTime.now().diff(date, ['days']);
  if (diff.days > 1) {
    result = date.toFormat('MMM d, h:mm a');
  }
  return result;
};

export const convertToAmPm = (timeString: string) => {
  return DateTime.fromFormat(timeString, 'HH:mm:ss').toFormat('hh:mm a');
};

export const formatTimestampWithMarketCloseThreshold = (
  timestamp: number | { valueOf: () => number } | undefined | null,
  displayFormat: string,
  timezone: string,
  hoursThreshold: number,
): string => {
  if (timestamp == null) return '';

  const normalizedTimestamp = typeof timestamp === 'object' ? Number(timestamp.valueOf()) : Number(timestamp);

  if (!Number.isFinite(normalizedTimestamp) || normalizedTimestamp <= 0) return '';

  const sourceZone = 'America/New_York';
  const displayZone = timezone || sourceZone;

  let dt: DateTime;
  const str = normalizedTimestamp.toString();

  if (str.length === 14) {
    dt = DateTime.fromFormat(str, 'yyyyMMddHHmmss', { zone: sourceZone }).setZone(displayZone);
  } else {
    const timeInMillis = normalizedTimestamp > 1e12 ? normalizedTimestamp : normalizedTimestamp * 1000;
    dt = DateTime.fromMillis(timeInMillis, { zone: displayZone });
  }

  if (!dt.isValid) return '';

  const now = DateTime.now().setZone(displayZone);
  const hoursDiff = now.diff(dt, 'hours').hours;

  if (hoursDiff > hoursThreshold) {
    return dt.toFormat(`LLL d, ${displayFormat} ZZZZ`);
  }

  return dt.toFormat(`${displayFormat} ZZZZ`);
};

export type TimeRange =
  | '1D'
  | '5D'
  | '1M'
  | '3M'
  | '90D'
  | '6M'
  | '12M'
  | 'YTD'
  | '1Y'
  | '2Y'
  | '5Y'
  | '10Y'
  | 'ALL'
  | 'MAX'
  | '50Y';

export const getDateByTimeframe = (timeframe: TimeRange): DateTime | undefined => {
  if (!timeframe) {
    return undefined;
  }

  const now = DateTime.now();

  switch (timeframe?.toUpperCase()) {
    case '1D':
      return now.minus({ days: 1 });
    case '5D':
      return now.minus({ days: 5 });
    case '1M':
      return now.minus({ months: 1 });
    case '3M':
      return now.minus({ months: 3 });
    case '90D':
      return now.minus({ days: 90 });
    case '6M':
      return now.minus({ months: 6 });
    case '180D':
      return now.minus({ days: 180 });
    case '12M':
    case '1Y':
      return now.minus({ years: 1 });
    case '2Y':
      return now.minus({ years: 2 });
    case '5Y':
      return now.minus({ years: 5 });
    case '10Y':
      return now.minus({ years: 10 });
    case 'YTD':
      return now.startOf('year');
    case 'ALL':
    case 'MAX':
    case '50Y':
      return DateTime.fromISO('1900-01-01');
    default:
      return undefined;
  }
};

/**
 * Converts a date string or number to a timestamp.
 * Handles various date formats from Google Sheets.
 * @param date - Date string, number (timestamp), or undefined
 * @returns Timestamp as number, or undefined if date is invalid/undefined
 */
export const parseDateToTimestamp = (date: string | number | undefined): number | undefined => {
  if (!date) return undefined;
  if (typeof date === 'number') return date;

  // Try parsing as ISO string or common date formats
  const parsed = new Date(date).getTime();
  return isNaN(parsed) ? undefined : parsed;
};
